import React, { useState } from 'react'
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import ImageIcon from '@material-ui/icons/Image'
import DescriptionIcon from '@material-ui/icons/Description'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  emptyRow: { textAlign: 'center', padding: theme.spacing(4) },
  inlineInput: {
    '& input': { padding: '4px 8px', fontSize: '0.875rem' }
  },
  productName: {
    fontWeight: 500,
    maxWidth: 250,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  specText: {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  },
  cutsheetThumb: {
    width: 40,
    height: 40,
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
    border: `1px solid ${theme.palette.divider}`
  },
  sourceChip: {
    height: 20,
    fontSize: '0.65rem'
  },
  docLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    marginRight: theme.spacing(1),
    '&:hover': { textDecoration: 'underline' }
  }
}))

const SOURCE_CHIP_COLORS = {
  vendor_catalog: '#1976d2',
  chemical: '#388e3c',
  material_catalog: '#f57c00',
  manual: '#999'
}

const SOURCE_LABELS = {
  vendor_catalog: 'Vendor',
  chemical: 'Chemical',
  material_catalog: 'Material',
  manual: 'Manual'
}

const SubmittalItemsTable = ({ items, onUpdateItem, onDeleteItem }) => {
  const classes = useStyles()
  const [editingCell, setEditingCell] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [cutsheetPreview, setCutsheetPreview] = useState(null)

  const startEdit = (itemId, field, currentValue) => {
    setEditingCell({ itemId, field })
    setEditValue(currentValue != null ? String(currentValue) : '')
  }

  const commitEdit = () => {
    if (!editingCell) return
    const { itemId, field } = editingCell
    const value = field === 'quantity' ? Number(editValue) || 0 : editValue
    onUpdateItem(itemId, { [field]: value })
    setEditingCell(null)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') setEditingCell(null)
  }

  const renderEditableCell = (item, field, displayValue) => {
    const isEditing = editingCell && editingCell.itemId === item._id && editingCell.field === field
    if (isEditing) {
      return (
        <TextField
          className={classes.inlineInput}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          autoFocus
          size="small"
          variant="outlined"
        />
      )
    }
    return (
      <span
        onClick={() => startEdit(item._id, field, displayValue)}
        style={{ cursor: 'pointer', minWidth: 30, display: 'inline-block' }}
      >
        {displayValue || '—'}
      </span>
    )
  }

  const renderDocsCell = (item) => {
    const hasCatalogPage = item.vendorCatalogId && item.pageNumber
    const hasDocUrls = item.documentUrls && item.documentUrls.length > 0

    if (hasCatalogPage) {
      return (
        <Tooltip title="View cutsheet page">
          <img
            className={classes.cutsheetThumb}
            src={`/api/vendor-catalogs/${item.vendorCatalogId}/page-image/${item.pageNumber}`}
            alt={`Page ${item.pageNumber}`}
            onClick={(e) => { e.stopPropagation(); setCutsheetPreview(item) }}
            onError={(e) => { e.target.style.display = 'none' }}
          />
        </Tooltip>
      )
    }

    if (hasDocUrls) {
      return (
        <div>
          {item.documentUrls.map((doc, i) => (
            <a
              key={i}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.docLink}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                window.open(doc.url, '_blank', 'noopener,noreferrer')
              }}
            >
              <DescriptionIcon style={{ fontSize: 14 }} />
              {doc.label || 'Doc'}
              <OpenInNewIcon style={{ fontSize: 10 }} />
            </a>
          ))}
        </div>
      )
    }

    return (
      <Tooltip title="No documents">
        <ImageIcon fontSize="small" color="disabled" />
      </Tooltip>
    )
  }

  if (items.length === 0) {
    return (
      <Paper>
        <Typography className={classes.emptyRow} color="textSecondary">
          No items yet. Click "Add Items" to search across vendor catalogs, chemicals, and materials.
        </Typography>
      </Paper>
    )
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}>#</TableCell>
              <TableCell width={60}>Docs</TableCell>
              <TableCell>Product</TableCell>
              <TableCell width={70}>Source</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Category</TableCell>
              <TableCell width={80}>Qty</TableCell>
              <TableCell width={60}>Unit</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={item._id} hover>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{renderDocsCell(item)}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {item.productImageUrl && (
                      <img
                        src={item.productImageUrl}
                        alt=""
                        style={{ width: 40, height: 40, objectFit: 'contain', marginRight: 8, borderRadius: 4, border: '1px solid #eee', flexShrink: 0 }}
                      />
                    )}
                    <div>
                      <div className={classes.productName}>{item.productName}</div>
                      {item.shortDescription && (
                        <div className={classes.specText}>{item.shortDescription}</div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Chip
                    className={classes.sourceChip}
                    label={SOURCE_LABELS[item.sourceType] || 'Manual'}
                    size="small"
                    style={{
                      backgroundColor: SOURCE_CHIP_COLORS[item.sourceType] || '#999',
                      color: '#fff'
                    }}
                  />
                </TableCell>
                <TableCell>{item.manufacturer || '—'}</TableCell>
                <TableCell>{item.vendorItemNumber || item.manufacturerPartNumber || '—'}</TableCell>
                <TableCell>{item.category || '—'}</TableCell>
                <TableCell>{renderEditableCell(item, 'quantity', item.quantity)}</TableCell>
                <TableCell>{renderEditableCell(item, 'unit', item.unit)}</TableCell>
                <TableCell>{renderEditableCell(item, 'notes', item.notes)}</TableCell>
                <TableCell>
                  <Tooltip title="Remove item">
                    <IconButton size="small" onClick={() => onDeleteItem(item._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cutsheet Preview Lightbox */}
      {cutsheetPreview && (
        <div
          onClick={() => setCutsheetPreview(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <img
            src={`/api/vendor-catalogs/${cutsheetPreview.vendorCatalogId}/page-image/${cutsheetPreview.pageNumber}`}
            alt={cutsheetPreview.productName}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
          />
        </div>
      )}
    </>
  )
}

export default SubmittalItemsTable
